import { useReadContracts, useChainId } from 'wagmi';
import { Address, erc20Abi, formatUnits, parseUnits } from 'viem';

/**
 * EquityToken-specific ABI for share ratio (extends ERC20).
 * The getTokenToShareRatio function returns the conversion ratio between tokens and underlying shares.
 *
 * NOTE: The deployed contract uses EquityToken.sol (not SecurityToken.sol).
 * EquityToken uses getTokenToShareRatio() with uint256 return types.
 * SecurityToken (newer, not yet deployed) uses shareRatio() with uint48 return types.
 *
 * TODO: Update to use SecurityToken ABI once the new contract is deployed:
 * - Change function name from 'getTokenToShareRatio' to 'shareRatio'
 * - Change return types from uint256 to uint48
 * - Add 'minimumRedeemable' function back to the ABI
 * @see https://github.com/coinbase/tokenization-protocol/blob/feature/etf/src/SecurityToken.sol
 */
const EQUITY_TOKEN_ABI = [
  {
    name: 'getTokenToShareRatio',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [
      { name: 'numerator', type: 'uint256' },
      { name: 'denominator', type: 'uint256' },
    ],
  },
] as const;

/**
 * Share ratio representing the conversion between tokens and underlying shares.
 * Initial state: 1:1 (1_000_000_000 / 1_000_000_000)
 * After 2:1 stock split: 2:1 (2_000_000_000 / 1_000_000_000)
 */
export type ShareRatio = {
  numerator: bigint;
  denominator: bigint;
};

export type EquityTokenData = {
  /** Token symbol (e.g., "tNVDA") */
  symbol: string | undefined;
  /** Token decimals (typically 18) */
  decimals: number | undefined;
  /** Raw token balance in wei */
  balance: bigint | undefined;
  /** Share ratio for token-to-share conversion */
  shareRatio: ShareRatio | undefined;
  /** Raw share balance in wei calculated from token balance and ratio */
  shareBalance: bigint | undefined;
  /**
   * Minimum redeemable amount in wei.
   * NOTE: Hardcoded to 1n as fallback since deployed EquityToken doesn't have this function.
   * TODO: Fetch from contract once SecurityToken is deployed.
   */
  minimumRedeemable: bigint;
  /** Whether data is still loading */
  isLoading: boolean;
  /** Error if any occurred during fetching */
  error: Error | null;
};

/**
 * Calculate share balance from token balance (bigint) using the ratio.
 *
 * Formula: shares = (tokenBalance * denominator) / numerator
 *
 * @param tokenBalance - Raw token balance in wei (bigint)
 * @param shareRatio - The share ratio from the contract
 * @returns Calculated share balance in wei (bigint), or undefined if inputs are missing
 *
 * @example
 * - 1:1 ratio (1B/1B): 1000 tokens = 1000 shares
 * - 2:1 reverse split (2B/1B): 1000 tokens = 500 shares
 * - 1:4 forward split (1B/4B): 1000 tokens = 4000 shares
 */
export function calculateShareBalance(
  tokenBalance: bigint | undefined,
  shareRatio: ShareRatio | undefined,
): bigint | undefined {
  if (tokenBalance === undefined || !shareRatio) return undefined;
  const { numerator, denominator } = shareRatio;
  if (numerator === 0n) return undefined;

  // shares = tokenBalance * denominator / numerator
  return (tokenBalance * denominator) / numerator;
}

/**
 * Calculate share amount from token amount string (for UI display).
 *
 * This is a convenience wrapper around calculateShareBalance that handles
 * string inputs and outputs for form fields.
 *
 * Formula: shares = (tokenAmount * denominator) / numerator
 *
 * @param tokenAmount - Human-readable token amount as string (e.g., "1000.5")
 * @param shareRatio - The share ratio from the contract
 * @param decimals - Token decimals (defaults to 18)
 * @returns Formatted share amount as string, or empty string if invalid
 *
 * @example
 * - Input: "1000" tokens, ratio 1:1 → Output: "1000"
 * - Input: "1000" tokens, ratio 2:1 → Output: "500"
 * - Input: "" (empty) → Output: ""
 */
export function calculateShareAmount(
  tokenAmount: string,
  shareRatio: ShareRatio | undefined,
  decimals = 18,
): string {
  if (!tokenAmount || !shareRatio || tokenAmount === '') return '';

  try {
    const tokenAmountBigInt = parseUnits(tokenAmount, decimals);
    const shareBalanceBigInt = calculateShareBalance(tokenAmountBigInt, shareRatio);

    if (shareBalanceBigInt === undefined) return '';

    return formatUnits(shareBalanceBigInt, decimals);
  } catch {
    return '';
  }
}

/**
 * Inverse of calculateShareAmount: converts share amount back to token amount.
 *
 * Formula: tokens = (shareAmount * numerator) / denominator
 *
 * @param shareAmount - Human-readable share amount as string (e.g., "500")
 * @param shareRatio - The share ratio from the contract
 * @param decimals - Token decimals
 * @returns Formatted token amount as string, or empty string if invalid
 *
 * @example
 * - Input: "500" shares, ratio 2:1 → Output: "1000"
 * - Input: "1000" shares, ratio 1:1 → Output: "1000"
 * - Input: "" (empty) → Output: ""
 */
export function calculateTokenAmount(
  shareAmount: string,
  shareRatio: ShareRatio | undefined,
  decimals?: number,
): string {
  if (!shareAmount || !shareRatio || shareAmount === '' || decimals === undefined) return '';

  const { numerator, denominator } = shareRatio;
  if (denominator === 0n) return '';

  try {
    const sharesBigInt = parseUnits(shareAmount, decimals);
    const tokensBigInt = (sharesBigInt * numerator) / denominator;
    return formatUnits(tokensBigInt, decimals);
  } catch {
    return '';
  }
}

/**
 * Format share ratio for display (e.g., "1:2" or "2:1").
 *
 * @param shareRatio - The share ratio from the contract
 * @returns Formatted ratio string (e.g., "1:1", "2:1", "1:4")
 */
export function formatShareRatio(shareRatio: ShareRatio | undefined): string {
  if (!shareRatio) return '...';
  const { numerator, denominator } = shareRatio;

  if (denominator === 0n) return '...';

  // Normalize to simplest form for display
  // Initial state: 1B/1B = 1:1
  const ratio = Number(numerator) / Number(denominator);
  if (ratio === 1) return '1:1';
  if (ratio > 1) return `${ratio.toFixed(0)}:1`;
  return `1:${(1 / ratio).toFixed(0)}`;
}

/**
 * Get security token metadata including share ratio from the token contract.
 *
 * The share ratio is stored directly on the SecurityToken contract as:
 * - numerator (uint48): Token amount in the ratio
 * - denominator (uint48): Share amount in the ratio
 *
 * Initial ratio is 1:1 (1B/1B), can change after stock splits.
 *
 * @param tokenAddress - The SecurityToken contract address
 * @param walletAddress - The wallet address to check balance for
 *
 * @example
 * ```tsx
 * function TokenInfo({ tokenAddress }: { tokenAddress: Address }) {
 *   const { address } = useAccount();
 *   const { symbol, balance, shareBalance, decimals, isLoading } = useEquityToken(tokenAddress, address);
 *
 *   if (isLoading) return <Spinner />;
 *
 *   return (
 *     <div>
 *       <p>Symbol: {symbol}</p>
 *       <p>Token Balance: {balance ? formatUnits(balance, decimals ?? 18) : '0'}</p>
 *       <p>Share Balance: {shareBalance ? formatUnits(shareBalance, decimals ?? 18) : '0'}</p>
 *     </div>
 *   );
 * }
 * ```
 */
export function useEquityToken(
  tokenAddress: Address | undefined,
  walletAddress: Address | undefined,
): EquityTokenData {
  const chainId = useChainId();

  const { data, isLoading, error } = useReadContracts({
    contracts: [
      {
        address: tokenAddress,
        abi: erc20Abi,
        functionName: 'symbol',
        chainId,
      },
      {
        address: tokenAddress,
        abi: erc20Abi,
        functionName: 'decimals',
        chainId,
      },
      {
        address: tokenAddress,
        abi: erc20Abi,
        functionName: 'balanceOf',
        args: walletAddress ? [walletAddress] : undefined,
        chainId,
      },
      {
        address: tokenAddress,
        abi: EQUITY_TOKEN_ABI,
        functionName: 'getTokenToShareRatio',
        chainId,
      },
    ],
    query: {
      enabled: Boolean(tokenAddress),
    },
  });

  const symbol = data?.[0]?.result;
  const decimals = data?.[1]?.result;
  const balance = data?.[2]?.result;
  const ratioResult = data?.[3]?.result as [bigint, bigint] | undefined;

  const shareRatio = ratioResult
    ? { numerator: ratioResult[0], denominator: ratioResult[1] }
    : undefined;

  const shareBalance = calculateShareBalance(balance, shareRatio);

  return {
    symbol,
    decimals,
    balance,
    shareRatio,
    shareBalance,
    minimumRedeemable: 1n,
    isLoading,
    error: error as Error | null,
  };
}
